import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box,  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import ArchivedReportPeriodService from '../../../shared/services/archived-report-period/archived-report-period.service';
import { IArchivedReportPeriod } from '../../../shared/services/archived-report-period/archived-report-period.model';
import { DialogSaveButtons } from '../../presentation-components/miscellaneous-components/dialog-save-buttons';

export const BCorpInfoDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  successHandler: React.Dispatch<React.SetStateAction<any>>,
  isSubmitting: boolean,
}> = ({
  open,
  closeHandler,
  successHandler,
  isSubmitting,
}) => {


    interface IDialogData {
      id: string;
      friendlyName: string;
      carbonCreditsTonnesCarbonEquivalent: number;
      totalRevenue: number;
      currencyCode?: string;
    }

    const archivedReportPeriodService = new ArchivedReportPeriodService();
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userDetailsContext = useContext(UserDetailsContext);
    const [dialogData, setDialogData] = useState<IDialogData[]>()
    const [archivedReportPeriods, setArchivedReportPeriods] = useState<IArchivedReportPeriod[]>()
    const [formattedArchivedReportPeriods, setFormattedArchivedReportPeriods] = useState<IDialogData[]>()

    useEffect(() => {
      getArchivedReportPeriods();
    }, [])

    const getArchivedReportPeriods = () => {
      archivedReportPeriodService.getAllArchivedReportPeriods()
        .then(response => {
          setArchivedReportPeriods(response)
          const formattedData: IDialogData[] = response.map(r => ({
            id: r.id,
            friendlyName: r.friendlyName,
            carbonCreditsTonnesCarbonEquivalent: r.carbonCreditsTCE,
            totalRevenue: r.totalRevenue,
            currencyCode: r.currencyCode,
          }))
          setFormattedArchivedReportPeriods(formattedData)
        })
        .catch(err =>
          console.log(err)
        )
    }

    useEffect(() => {
      const formattedReportPeriod: IDialogData[] = reportPeriodContext.allReportPeriods?.map(r => ({
        id: r.id,
        friendlyName: r.friendlyName,
        carbonCreditsTonnesCarbonEquivalent: r.carbonCreditsTonnesCarbonEquivalent,
        totalRevenue: r.totalRevenue,
        currencyCode: r.currencyCode,
      }))

      if (formattedArchivedReportPeriods) {
        setDialogData([...formattedReportPeriod, ...formattedArchivedReportPeriods])
      }

    }, [formattedArchivedReportPeriods, reportPeriodContext.allReportPeriods])

    const bcorpSchema = Yup.object().shape({
      reportPeriodId: Yup.string().required('You must select a report period'),
      renewableEnergyGj: Yup.number(),
      emissionOffsetTonnes: Yup.number(),
      totalRevenue: Yup.number().required('You must provide your total revenue'),
    })

    const formik = useFormik({
      initialValues: {
        reportPeriodId: '',
        renewableEnergyGj: undefined,
        emissionOffsetTonnes: undefined,
        totalRevenue: undefined,
      },
      validationSchema: bcorpSchema,
      onSubmit: (values) => {
        successHandler(values)
      }
    });

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    const applyPeriodSelection = (periodId: string) => {
      const selectedReportPeriod = dialogData?.find(reportPeriod => reportPeriod.id === periodId)
      if (selectedReportPeriod) {
        if (selectedReportPeriod.carbonCreditsTonnesCarbonEquivalent) {
          formik.setFieldValue('emissionOffsetTonnes', selectedReportPeriod.carbonCreditsTonnesCarbonEquivalent)
        } else {
          formik.setFieldValue('emissionOffsetTonnes', undefined)
        }
        if (selectedReportPeriod.totalRevenue) {
          formik.setFieldValue('totalRevenue', selectedReportPeriod.totalRevenue)
        } else {
          formik.setFieldValue('totalRevenue', undefined)
        }
      }
      formik.setFieldValue('reportPeriodId', periodId)
    }

    const selectHandler = (e: SelectChangeEvent<string>) => {
      applyPeriodSelection(e.target.value)
    }

    // Default to the user's last selected reporting period (still editable), pre-filling
    // offsets and revenue the same way a manual selection would.
    useEffect(() => {
      if (!open || !dialogData) return
      if (formik.values.reportPeriodId) return
      const defaultId = reportPeriodContext.lastSelectedReportId
      if (defaultId && dialogData.some(period => period.id === defaultId)) {
        applyPeriodSelection(defaultId)
      }
    }, [open, dialogData, reportPeriodContext.lastSelectedReportId])

    // B Corp revenue is reported in the period's own currency, so label the field with it
    // rather than assuming any one currency. Falls back to the organisation's currency
    // before a period is chosen, then to a neutral label.
    const currencyCode = dialogData?.find(period => period.id === formik.values.reportPeriodId)?.currencyCode
      || userDetailsContext.userDetails?.organisation?.currencyCode
    const totalRevenueLabel = currencyCode ? `Total Revenue (${currencyCode})` : 'Total Revenue'

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Please provide the following information
          </DialogTitle>
          {dialogData && <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='reportPeriodId'>Report Period *</InputLabel>
              <Select id='reportPeriodId' name='reportPeriodId' label='report period  *'
                value={formik.values.reportPeriodId}
                error={formik.touched.reportPeriodId && Boolean(formik.errors.reportPeriodId)}
                onChange={selectHandler}
              >
                {dialogData.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)).map((reportPeriod, index) => (
                  <MenuItem key={index} value={reportPeriod.id}>{reportPeriod.friendlyName}</MenuItem>
                ))}
              </Select>
              {formik.touched.reportPeriodId && formik.errors.reportPeriodId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.reportPeriodId}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='renewableEnergyGj' >Total Renewable Energy Use</InputLabel>
              <OutlinedInput id='renewableEnergyGj' name='renewableEnergyGj' type='number' label='Total renewable energy use'
                value={formik.values.renewableEnergyGj || ''}
                onChange={formik.handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>GJ</Typography></InputAdornment>}
                error={formik.touched.renewableEnergyGj && Boolean(formik.errors.renewableEnergyGj)}
              ></OutlinedInput>
              <Stack justifyContent='start' alignItems='start'>
                {formik.touched.renewableEnergyGj && formik.errors.renewableEnergyGj &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.renewableEnergyGj}</FormHelperText>
                }
              </Stack>
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='emissionOffsetTonnes' >Total Offsets</InputLabel>
              <OutlinedInput id='emissionOffsetTonnes' name='emissionOffsetTonnes' type='number' label='Total Offsets'
                value={formik.values.emissionOffsetTonnes || ''}
                onChange={formik.handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
                error={formik.touched.emissionOffsetTonnes && Boolean(formik.errors.emissionOffsetTonnes)}
              ></OutlinedInput>
              <Stack justifyContent='start' alignItems='start'>
                {formik.touched.emissionOffsetTonnes && formik.errors.emissionOffsetTonnes &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.emissionOffsetTonnes}</FormHelperText>
                }
              </Stack>
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='totalRevenue' >{`${totalRevenueLabel} *`}</InputLabel>
              <OutlinedInput id='totalRevenue' name='totalRevenue' type='number' label={`${totalRevenueLabel} *`}
                value={formik.values.totalRevenue || ''}
                onChange={formik.handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.totalRevenue && Boolean(formik.errors.totalRevenue)}
              ></OutlinedInput>
              <Stack justifyContent='start' alignItems='start'>
                {formik.touched.totalRevenue && formik.errors.totalRevenue &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalRevenue}</FormHelperText>
                }
                {/* <FormHelperText sx={{ color: 'text.secondary', marginLeft: "16px !important" }}>The currency of the country where your organisation is headquatered will be used </FormHelperText> */}
              </Stack>
            </FormControl>
          </DialogContent>}
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }